<template>
  <section>
    <draggable ghost-class="ghost" :list="selectedSection.options" @start="isDragging = true" @end="isDragging = false">
      <transition-group type="transition" name="list-complete">    
        <div 
          v-for="(opt, index) in selectedSection.options" 
          :key="index" 
          class="show-cursor bgHover"
        >
          <Expansion 
            v-model="opt.open" 
            :verticalExpansion="false"
            @click="onOpen(index, opt.open)"
          >
            <template slot="default">
              <section class="d-flex flex-column mb-2">
                <div class="styles-item">
                  <expanded-icon :isExpanded="opt.open"/>
                  <span :style="`font-weight: ${opt.open ? '700' : '400'}`">Opção {{index + 1}}</span>
                  <div class="d-flex flex justify-end">
                    <DeleteButton @click="handleDelete(selectedSection.options, index)"/>       
                  </div>
                </div>   
              </section>         
            </template>

            <template slot="expanded">
              <v-row no-gutters class="px-3 pb-6">
                <v-col cols="6" class="_pr-">
                  <TextField v-model="opt.caption" label="Conteúdo"/>        
                </v-col>

                <v-col cols="6" class="_pl-">
                  <Select
                    v-model="opt.style"
                    label="Estilo"
                    item-text="label"
                    item-value="value"
                    :items="styles"
                    :showBorder="true"
                  />        
                </v-col>

                <v-col v-if="opt.style === 'button'" cols="4" class="_pr- _pt">
                  <ColorPickerV2 :colorSelected="opt" type="textColor" title="Cor da fonte" compact/>
                </v-col> 

                <v-col v-if="opt.style === 'button'" cols="4" class="_pl- _pt">
                  <ColorPickerV2 :colorSelected="opt" type="color" title="Cor fundo" compact/>
                </v-col> 

                <v-col v-if="opt.style === 'button'" cols="4" class="pl-2 _pt">
                  <TextField 
                    v-model="opt['border-radius']"
                    placeholder="px"
                    type="number"
                    label="Borda"
                    :minValueForNumber="0"
                  />  
                </v-col> 

                <v-col cols="12" class="_pt">
                  <Select
                    v-model="opt.destination"
                    :icon="getDestinationIcon(opt)"
                    :items="destinations"
                    :showBorder="true"
                    @change="changeDestination(opt)"
                  />
                </v-col>

                <v-col v-if="opt.destination === 'Seção'" cols="12" class="_pt">
                  <Select
                    v-model="opt.action"
                    placeholder="Escolha a seção destino"
                    item-text="title"
                    item-value="id"            
                    :items="availableSectionsToNavigate"
                    :showBorder="true"
                  />
                </v-col>

                <v-col v-if="opt.destination === 'Página'" cols="12" class="_pt">
                  <Select
                    v-model="opt.action"
                    placeholder="Escolha a página destino"
                    item-text="name"
                    item-value="id"            
                    :items="getPages"
                    :showBorder="true"
                  />
                </v-col>

                <v-col v-if="opt.destination === 'Link Externo'" cols="12" class="_pt">
                  <TextField placeholder="https://" v-model="opt.action"/>
                </v-col>              
              </v-row>
            </template>
          </Expansion>          
        </div>
      </transition-group>
    </draggable>        

    <v-row no-gutters class="d-flex flex-column btn py-2">
      <Button 
        label="Adicionar opção" 
        color="bt_gray_bg"
        textColor="darkBlue"
        textSize="0.80rem"
        icon="fa-solid fa-plus"
        @click="handleAdd"
      />      
    </v-row>
  </section>    
</template>

<script>
import { mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import { parseObject } from '../../../../schemas'
import { MENU_OPTION } from '../../../../schemas/sections'
import Select from '../../../_v2/ui/Select.vue'
import TextField from '../../../_v2/ui/TextField.vue'
import ColorPickerV2 from '../../../_v2/ui/ColorPickerV2.vue'
import FormAlignment from '../../../_v2/utils/FormAlignment.vue'
import ExpandedIcon from '../../../_v2/utils/ExpandedIcon.vue'
import Expansion from '../../../_v2/ui/Expansion.vue'
import Button from '../../../_v2/ui/Button.vue'
import DeleteButton from '../../../_v2/ui/DeleteButton.vue'

export default {
  name: 'MenuOptions',
  components: {draggable, Select, TextField, ColorPickerV2, FormAlignment, Button, DeleteButton, Expansion, ExpandedIcon},
  props: {
    selectedSection: {type: Object, required: true},
    sectionsId: {type: Array, required: true},
  },

  computed: {
    ...mapGetters(['infos']),

    hasBindedStyle () {
      return this.selectedSection.idLayout ? true : false
    },

    availableSectionsToNavigate () {
      return this.sectionsId.filter(item => !['menu', 'footer'].includes(item.title))
    },

    getPages () {
      return this.infos.pages
    }
  },   

  data: () => ({
    styles: [{value: 'text', label: 'Texto'}, {value: 'button', label: 'Botão'}],
    destinations: ["Seção", 'Página', "Link Externo", "Abrir Checkout"],
    isDragging: false
  }),  

  beforeDestroy () {
    this.selectedSection.options.forEach(item => { item.open = false })
  },     

  methods: {
    getDestinationIcon (opt) {
      if (opt.destination === 'Seção') return 'fa-solid fa-arrow-up-right-from-square'
      if (opt.destination === 'Link Externo') return 'fa-solid fa-link'
      if (opt.destination === 'Abrir Checkout') return 'fa-solid fa-cart-shopping'
      if (opt.destination === 'Página') return 'fa-solid fa-arrow-up-right-from-square'
    },

    changeDestination(opt) {
      if (opt.destination === "Abrir Checkout") opt.action = "Abrir Checkout"
      else opt.action = ""
    },

    onOpen (index, state) {
      if (state && this.selectedSection.options && this.selectedSection.options.length > 0) {
        this.selectedSection.options.forEach((option, i) => {
          if (i !== index) option.open = false
        })
      }
    },    

    handleDelete(options, index) {
      options.splice(index, 1)
    },

    handleAdd () {
      const previousBtn = this.selectedSection.options.find(btn => btn.style === 'button')

      const newOption = {
        action: '',
        caption: `Opção ${this.selectedSection.options.length + 1}`,
        destination: 'Seção',
        style: 'text',
        color: previousBtn ? previousBtn.color : null,
        textColor: previousBtn ? previousBtn.textColor : null,
        'border-radius': previousBtn ? previousBtn['border-radius'] : null,
        open: false
      }

      const newOptionParsed = parseObject({schema: MENU_OPTION(), target: newOption})
      newOptionParsed.open = true

      this.selectedSection.options.push(newOptionParsed)
    }
  }
}
</script>

<style scoped>
.btn {
  transition: all 0.3s;
}

.btn button:hover {
  background-color: #CBCBCE !important;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
  cursor: move !important;
}

  .styles-item {
    display: flex;
    gap: 10px;
    height: 50px;
    align-items: center;
    font-size: 0.9rem;
    transition: all 0.3s;
    padding: 0 8px 0 8px;
    border-radius: 4px;
    overflow: hidden;
    white-space: nowrap;
  }

  .styles-item span {
    max-width: 70%;
    overflow: hidden;
    text-overflow: ellipsis;  
  }

  .styles-item:hover {
    cursor: pointer;
    animation: changeCursor 3s forwards;
  }

  .show-cursor {
    cursor: move !important;
    transition: all 0.3s;    
  }

  .show-cursor:hover {
    border-radius: 4px;
  }

  .bgHover:hover {
    background-color: #FAFBFB;    
  }

  @keyframes changeCursor {
    100% {
      cursor: move;
    }
  }

.list-complete-item {
  transition: all 0.8s;
  display: inline-block;
  margin-right: 10px;
}

.list-complete-enter, .list-complete-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.list-complete-leave-active {
  position: absolute;
  display: flex;
  align-items: center;
  width: 100%;
  height: 50px;
}
</style>